import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LOCALE_NAMES, SUPPORTED_LOCALES, type Locale } from '@snakefeast/shared';
import en from './locales/en.json';

const RTL = new Set(['ar']);

/** English ships in the main bundle; other languages are fetched on demand (~3 KB each). */
const loaders: Record<Exclude<Locale, 'en'>, () => Promise<{ default: Record<string, unknown> }>> = {
  es: () => import('./locales/es.json'),
  pt: () => import('./locales/pt.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  hi: () => import('./locales/hi.json'),
  ar: () => import('./locales/ar.json'),
  ja: () => import('./locales/ja.json'),
};

/** The player's saved choice, else `preferred` (e.g. a game portal's locale), else the browser's languages. */
export function detectLocale(preferred: string | null = null): Locale {
  const saved = localStorage.getItem('snakefeast.locale');
  if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) return saved as Locale;
  for (const l of [...(preferred ? [preferred] : []), ...(navigator.languages ?? [navigator.language])]) {
    const base = l.slice(0, 2).toLowerCase();
    if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) return base as Locale;
  }
  return 'en';
}

/** `remember: false` for a language that only comes from the URL ("/hi"), not the player's own choice. */
export async function setLocale(locale: Locale, { remember = true }: { remember?: boolean } = {}): Promise<void> {
  if (locale !== 'en' && !i18n.hasResourceBundle(locale, 'translation')) {
    const mod = await loaders[locale]();
    i18n.addResourceBundle(locale, 'translation', mod.default, true, true);
  }
  await i18n.changeLanguage(locale);
  try {
    if (remember) localStorage.setItem('snakefeast.locale', locale);
  } catch {
    /* private mode */
  }
  document.documentElement.lang = locale;
  document.documentElement.dir = RTL.has(locale) ? 'rtl' : 'ltr';
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

export { LOCALE_NAMES };

export default i18n;
